import { Button } from "./ui/button";
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from "lucide-react";
import TrustBadges from "./TrustBadges";

const ContactSection = () => {
  return (
    <section className="py-16" id="contact">
      <div 
        className="border border-[hsl(var(--line))] rounded-xl p-8 md:p-12"
        style={{
          background: "linear-gradient(180deg, hsl(var(--glass)), hsl(0 0% 100% / 0.03))"
        }}
      >
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-orbitron font-bold text-3xl md:text-4xl mb-4">
              Connect with Our <span className="text-primary">AI Voice Agent</span>
            </h2>
            <p className="text-muted-foreground mb-6">
              Experience our 24/7 AI-powered voice assistant. Talk to our intelligent agent to discuss automation solutions, get instant quotes, and schedule consultations.
            </p>

            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3 p-4 border border-primary/30 bg-primary/5 rounded-lg">
                <Phone className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold text-primary mb-1">AI Voice Agent Available 24/7</p>
                  <a 
                    href="tel:+13369624539"
                    className="text-lg font-bold hover:text-primary transition-colors"
                  >
                    (336) 962-4539
                  </a>
                  <p className="text-xs text-muted-foreground mt-1">Instant answers · Smart scheduling · Live support</p>
                </div>
              </div>

              <a 
                href="mailto:neilai@neilaiglobalsolutionsllc.com"
                className="flex items-center gap-3 hover:text-primary transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span className="font-semibold">neilai@neilaiglobalsolutionsllc.com</span>
              </a>

              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="font-semibold">611 Summit Ave, Suite 14</p>
                  <p className="text-muted-foreground">Greensboro, NC 27405</p>
                </div>
              </div>
            </div>

          <div className="flex gap-3 flex-wrap">
            <a href="tel:+13369624539" className="inline-block">
              <Button 
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-extrabold rounded-xl"
              >
                Call AI Voice Agent Now
              </Button>
            </a>
            <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer" className="inline-block">
              <Button 
                size="lg"
                variant="outline"
                className="glass-effect border-[hsl(var(--line))] hover:bg-white/10 font-bold rounded-xl"
              >
                Book Live Consultation
              </Button>
            </a>
          </div>
          </div>

          <div 
            className="rounded-xl p-8 border border-[hsl(var(--line))] bg-white/5"
          >
            <h3 className="font-orbitron font-bold text-xl mb-4">Business Hours</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monday - Friday</span>
                <span className="font-semibold">9:00 AM - 6:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Saturday</span>
                <span className="font-semibold">10:00 AM - 4:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sunday</span>
                <span className="font-semibold">Closed</span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[hsl(var(--line))]">
              <p className="text-xs text-muted-foreground mb-3">Follow Us</p>
              <div className="flex gap-3">
                <a 
                  href="https://www.facebook.com/share/17XtMmjseX/?mibextid=wwXIfr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-[hsl(var(--line))] glass-effect hover:bg-white/10 transition-all"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.linkedin.com/in/albert-saunders-62a26137a?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-[hsl(var(--line))] glass-effect hover:bg-white/10 transition-all"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.instagram.com/neil_ai500?igsh=MW5idHpoejlpMjB1ZA%3D%3D&utm_source=qr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-[hsl(var(--line))] glass-effect hover:bg-white/10 transition-all"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href="https://tiktok.com/@neilaiglobalsolutions" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-[hsl(var(--line))] glass-effect hover:bg-white/10 transition-all"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 pt-8 border-t border-[hsl(var(--line))]">
          <TrustBadges />
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
